import { Routes } from '@angular/router';
import { LoginComponent } from './Components/login/login.component';
import { ForgotPasswordComponent } from './Components/forgot-password/forgot-password.component';
import { userGuard } from './Services/user.guard';
import { spendkeyStaffGuard } from './Services/spendkey-staff.guard';
import { provideWorkflowFeature } from './Store/workflow';
import { superAdminGuard } from './Services/super-admin.guard';
import { varianceAnalysisGuard } from './Services/variance-analysis.guard';
import { maverickSpendGuard } from './Services/maverick-spend.guard';
import { controlTowerGuard } from './Services/control-tower.guard';
import { spendExplorerGuard } from './Services/spend-explorer.guard';
import { savingsTrackerGuard } from './Services/savings-tracker.guard';
import { reportDataUploadsGuard } from './Services/report-data-uploads.guard';
import { htmlPageGuard } from './Services/html-page.guard';
import { reclassificationGuard } from './Services/reclassification.guard';
import { reconciliationReviewGuard } from './Services/savings-reconciliation-review.guard';
import { savingsRecommendationsGuard } from './Services/savings-recommendations.guard';
import { ExecutiveOverviewComponent } from './Components/executive-overview/executive-overview.component';
import { SpendAnalyticsComponent } from './Components/spend-analytics/spend-analytics.component';
import { OktaCallbackHandlerComponent } from './Components/okta-callback/okta-callback-handler.component';
import { PingCallbackHandlerComponent } from './Components/ping-callback/ping-callback.component';
import { PRICING_INDEX_PROFILE } from './Components/pricing-index-dashboard/profile/pricing-index-profile';
import { MEPPI_PROFILE } from './Components/pricing-index-dashboard/profile/meppi-profile';
import { DEMO_PROFILE } from './Components/pricing-index-dashboard/profile/demo-profile';
import { REPORT_DESTINATION_ROUTES } from './Components/report-view/report-view.routes';

export const routes: Routes = [
  {
    path: 'home',
    loadComponent: () => import('./Components/home/home.component').then(auth => auth.HomeComponent),
  },
  {
    // Per-user aging monitor — the logged-in user's own pending/overdue approvals
    // (US #9320). Any authenticated user; the queue itself is empty for non-approvers.
    // No userGuard — that guard blocks Member-role users, which excludes the exact
    // finance-controller / reviewer personas whose R-12 inbox this route surfaces (#10178).
    path: 'my-approvals',
    // Register the workflow slice + effects lazily for this route (same as the
    // admin Workflow route) — the inbox selectors read state.workflow.inbox.
    providers: [provideWorkflowFeature()],
    loadComponent: () => import('./Components/my-approvals/my-approvals.component').then(m => m.MyApprovalsComponent),
  },
  {
    // PBF-01 (#9702) Planning, Budgeting & Forecasting (savings-first). No feature-gate guard
    // yet — reachable via the dev-menu sidebar entry while the tblPage row (#9834) and a
    // planning guard don't exist. Add the guard + tblPage row before production.
    path: 'planning',
    loadComponent: () => import('./Components/planning/planning-page/planning-page.component').then(m => m.PlanningPageComponent),
  },
  {
    path: 'executive-overview',
    loadComponent: () => import('./Components/executive-overview/executive-overview.component').then(auth => auth.ExecutiveOverviewComponent),
  },
  {
    path: 'contract-detail-view',
    loadComponent: () => import('./Components/contract-detail-view/contract-detail-view.component').then(auth => auth.ContractDetailViewComponent),
  },
  {
    path: 'agreement-overview',
    loadComponent: () => import('./Components/agreement-overview/agreement-overview.component').then(auth => auth.AgreementOverviewComponent),
  },
  {
    path: 'contract-analytics',
    loadComponent: () => import('./Components/contract-analysis/contract-analysis.component').then(auth => auth.ContractAnalysisComponent),
    children: [
      {
        path: 'overview',
        loadComponent: () => import('./Components/contract-overview-dashboard/contract-overview-dashboard.component').then(auth => auth.ContractOverviewDashboardComponent),
      },
      {
        path: 'licensing',
        loadComponent: () => import('./Components/licensing-contracts-overview/licensing-contracts-overview.component').then(auth => auth.LicensingContractsOverviewComponent),
      },
    ],
  },
  {
    path: '2-way-mapping',
    loadComponent: () => import('./Components/two-way-mapping/two-way-mapping.component').then(auth => auth.TwoWayMappingComponent),
    children: [
      {
        path: 'key-metrics',
        loadComponent: () => import('./Components/key-metrics-two-way/key-metrics-two-way.component').then(auth => auth.KeyMetricsTwoWayComponent),
      },
      {
        path: 'invoice-control',
        loadComponent: () => import('./Components/invoice-control-dashboard-two-way/invoice-control-dashboard-two-way.component').then(auth => auth.InvoiceControlDashboardTwoWayComponent),
      },
    ],
  },
  {
    path: '3-way-mapping',
    loadComponent: () => import('./Components/three-way-mapping/three-way-mapping.component').then(m => m.ThreeWayMappingComponent),
    children: [
      { path: '', redirectTo: 'key-metrics', pathMatch: 'full' },
      {
        path: 'key-metrics',
        loadComponent: () => import('./Components/key-metrics-three-way/key-metrics-three-way.component').then(m => m.KeyMetricsThreeWayComponent),
      },
      {
        path: 'invoice-control',
        loadComponent: () => import('./Components/invoice-control-dashboard-three-way/invoice-control-dashboard-three-way.component').then(m => m.InvoiceControlDashboardThreeWayComponent),
      },
    ],
  },
  {
    path: 'licenses',
    loadComponent: () => import('./Components/licenses/licenses.component').then(m => m.LicensesComponent),
  },
  {
    path: 'budget',
    loadComponent: () => import('./Components/budget/budget/budget.component').then(m => m.BudgetComponent),
  },
  {
    path: 'Budget-Dashboard',
    loadChildren: () => import('./Components/budget-dashboard/budget-dashboard.module').then(auth => auth.BudgetDashboardModule),
  },
  {
    path: 'setBudget',
    loadChildren: () => import('./Components/SetBudgetManualEntry/budget-manual-entry-routing.module').then(auth => auth.BudgetManualEntryRoutingModule),
  },
  {
    path: 'forgot-password',
    component: ForgotPasswordComponent
  },
  {
    path: 'new-password',
    loadChildren: () => import('./Components/new-password/new-password.module').then(auth => auth.NewPasswordModule)
  },
  {
    path: 'login',
    component: LoginComponent
  },
  {
    path: 'okta/callback',
    component: OktaCallbackHandlerComponent
  },
  {
    path: 'ping/callback',
    component: PingCallbackHandlerComponent
  },
  {
    path: 'company-onboarding',
    loadComponent: () => import('./Components/company-onboarding/company-onboarding.component').then(m => m.CompanyOnboardingComponent),
  },
  {
    path: 'Forex-Rate',
    loadChildren: () => import('./Components/currency-converter/currency-converter.module').then(auth => auth.CurrencyConverterModule),
  },
  {
    path: 'variance-analysis',
    loadComponent: () => import('./Components/savings-tracker/variance/variance-analysis-page/variance-analysis-page.component').then(m => m.VarianceAnalysisPageComponent),
    canActivate: [varianceAnalysisGuard],
  },
  {
    // UC-04 (#9281) — maverick spend exception queue.
    path: 'maverick-spend',
    loadComponent: () => import('./Components/maverick-spend/maverick-spend.component').then(m => m.MaverickSpendComponent),
    canActivate: [maverickSpendGuard],
  },
  {
    // UC-16 (#9297) — global supplier status control tower.
    path: 'control-tower',
    loadComponent: () => import('./Components/control-tower/control-tower.component').then(m => m.ControlTowerComponent),
    canActivate: [controlTowerGuard],
  },
  {
    // Spend Explorer — a read-only browser over the central spend table, with a
    // column-pickable Excel export. Which table it reads is named inside the
    // SP_Get_SpendExplorer_* procs, so repointing it never touches this route.
    path: 'spend-explorer',
    loadComponent: () =>
      import('./Components/spend-explorer/spend-explorer.component').then(m => m.SpendExplorerComponent),
    canActivate: [spendExplorerGuard],
  },
  {
    // Report Data Uploads — the finance, ESG and CO2 workbooks that feed the
    // reporting dashboards. Feeds and their permissions are configured in
    // tblReportDataFeed, so adding an upload type needs no route change.
    path: 'report-data-uploads',
    loadComponent: () =>
      import('./Components/report-data-uploads/report-data-uploads.component').then(
        m => m.ReportDataUploadsComponent,
      ),
    canActivate: [reportDataUploadsGuard],
  },
  {
    // A reference document authored outside the app, uploaded to blob storage and served through
    // the API. Publishing another one is a config entry plus a tblPage row, not a route change —
    // this entry exists per document only because each has its own permission key and nav label.
    path: 'kpi-dictionary',
    loadComponent: () =>
      import('./Components/html-page/html-page.component').then(m => m.HtmlPageComponent),
    canActivate: [htmlPageGuard],
    data: {
      htmlPage: {
        pageName: 'KpiDictionary',
        titleKey: 'KPI_DICTIONARY',
        permissionKey: 'kpi-dictionary.view',
      },
    },
  },
  {
    // UC-02 (#9279) — RETIRED ROUTE, TRANSITIONAL REDIRECT.
    //
    // The savings-opportunity worklist and its ranking config are now chip-tabs
    // on the savings-tracker shell. This redirect exists only for the window in
    // which a provisioned tenant still has tblPage row 54 at Status=1: the
    // sidebar builds its entries from that row's [Link] and calls
    // navigateByUrl('savings-opportunity'), so without this the entry would be a
    // dead link. Query params are preserved so ?opportunityId=<id> still opens
    // the right drawer.
    //
    // DELETE THIS once every provisioned tenant has run
    // SavingsTrackerScripts/RESTRUCTURE_03_OpportunityTabGate.sql (which sets
    // rows 54/57 to Status=0). Nothing else links here — no notification or
    // email template builds this URL.
    //
    // A plain path redirect: `redirectTo` takes a path, not a query string, so
    // the tab is not selected here. The shell defaults to the Opportunities tab
    // whenever `?opportunityId=` arrives without an explicit `?tab=`, which
    // covers the deep-link case; the bare sidebar link carries no params and
    // lands on the default tab, which is the intended graceful outcome.
    path: 'savings-opportunity',
    redirectTo: 'savings-tracker',
  },
  {
    // UC-20 (#9293) — Finance realised-value reconciliation review queue.
    // Top-level route so the sidebar's Link ('savings-reconciliation-review')
    // resolves without a nested prefix. Tenant-gated via tblPage.ReconciliationReview;
    // component still enforces the Finance persona via RBAC (`savings.approve`).
    path: 'savings-reconciliation-review',
    canActivate: [reconciliationReviewGuard],
    loadComponent: () =>
      import('./Components/savings-tracker/reconciliation-review/reconciliation-review.component')
        .then(m => m.ReconciliationReviewComponent),
  },
  {
    // US #10335 task 1.6 (Bundle C polish, US #10339). Spendkey-internal
    // tenant admin surface for savings-tracker config. Three-layer visibility:
    //   1. Sidebar filter — hidden for non-Spendkey users (INTERNAL_ONLY_SIDEBAR_LINKS)
    //   2. Route guard — `spendkeyStaffGuard()` (Spendkey staff, any role)
    //   3. Backend — `[RequireSpendkeyStaff]` returns envelope-403
    // No fine-grained RBAC layered on top; the domain gate is the sole
    // access control. The old savings.settings.view / .manage keys retired.
    // (Pre-polish, this route had NO guard despite a comment claiming userGuard —
    // fixed inline here.)
    path: 'savings-settings',
    canActivate: [spendkeyStaffGuard()],
    loadComponent: () =>
      import('./Components/savings-settings/savings-settings.component')
        .then(m => m.SavingsSettingsComponent),
  },
  {
    // UC-06 (#9283) — printable variance pack per initiative.
    // Opens in a new tab so the user can Ctrl+P to PDF without app chrome.
    path: 'variance-pack/:savingItemId',
    loadComponent: () => import('./Components/savings-tracker/variance/variance-pack/variance-pack.component').then(m => m.VariancePackComponent),
    canActivate: [varianceAnalysisGuard],
  },
  {
    // UC-06 (#9620) — portfolio variance pack. Aggregates one summary row per
    // initiative across the tenant, normalised to the tenant reporting currency.
    // Optional `?initiativeIds=1,2,3` filters; omitted = every initiative
    // returned by getItemList (subject to RBAC on the per-initiative reads).
    path: 'variance-pack-portfolio',
    loadComponent: () => import('./Components/savings-tracker/variance/variance-pack-portfolio/variance-pack-portfolio.component').then(m => m.VariancePackPortfolioComponent),
    canActivate: [varianceAnalysisGuard],
  },
  {
    path: 'fx-rate-admin',
    loadComponent: () => import('./Components/fx-rate-admin/fx-rate-admin.component').then(m => m.FxRateAdminComponent),
    canActivate: [userGuard],
  },
  {
    path: 'kpi-scorecard',
    loadComponent: () => import('./Components/kpi-scorecard/kpi-scorecard.component').then(m => m.KpiScorecardComponent),
    canActivate: [userGuard],
  },
  {
    path: 'kpi-scorecard/drill',
    loadComponent: () => import('./Components/kpi-driver-data/kpi-driver-data.component').then(m => m.KpiDriverDataComponent),
    canActivate: [userGuard],
  },
  {
    path: 'kpi-target-admin',
    loadComponent: () => import('./Components/kpi-target-admin/kpi-target-admin.component').then(m => m.KpiTargetAdminComponent),
    canActivate: [userGuard],
  },
  {
    path: 'ITOpexBudget',
    loadChildren: () => import('./Components/ITOpexPage/ITOpexBudget-routing.module').then(auth => auth.ITOpexBudgetRoutingModule)
  },
  {
    path: 'MIS-Dashboard',
    loadChildren: () => import('./Components/mis-dashboard/mis-dashboard.module').then(auth => auth.MisDashboardModule),
  },
  {
    path: 'keyManagement',
    loadChildren: () => import('./Components/key-management/key-management.module').then(auth => auth.KeyManagementModule),
  },
  {
    path: 'Contract-Dashboard',
    loadChildren: () => import('./Components/contract-dashboard/contract-dashboard.module').then(auth => auth.ContractDashboardModule)
  },
  {
    path: 'savings-tracker',
    canActivate: [savingsTrackerGuard],
    loadChildren: () => import('./Components/savings-tracker/savings-tracker.module').then(auth => auth.SavingsTrackerModule)
  },
  {
    path: 'ProductServices',
    loadChildren: () => import('./Components/product-services/product-services.module').then(auth => auth.ProductServicesModule)
  },
  {
    path: 'resellers',
    loadChildren: () => import('./Components/reseller-contract/reseller-contract.module').then(auth => auth.ResellerContractModule),
  },
  {
    path: 'edit-contract',
    loadComponent: () => import('./Components/contract/contract-edit/contract-edit.component').then(m => m.ContractEditComponent),
  },
  {
    path: 'view-contract',
    loadComponent: () => import('./Components/contract/view-contract/view-contract.component').then(m => m.ViewContractComponent),
  },
  {
    // PowerBI Spend Dashboard tile-hyperlinks navigate here. Path is mixed-case
    // ('view-PDF') to match the URL the PBI report emits verbatim — Angular
    // route matching is case-sensitive.
    path: 'view-PDF',
    loadComponent: () => import('./Components/view-pdf-pb/view-pdf-pb.component').then(m => m.ViewPdfPbComponent),
  },
  {
    path: 'find-in-contract',
    loadComponent: () => import('./Components/contract/find-in-contract/find-in-contract.component').then(m => m.FindInContractComponent),
  },
  {
    path: 'contextual-search',
    loadComponent: () => import('./Components/contract/contextual-search/contextual-search.component').then(m => m.ContextualSearchComponent),
  },
  {
    path: 'contracts',
    loadChildren: () => import('./Components/contract/contract.module').then(auth => auth.ContractModule),
  },
  {
    path: 'add-new-contract',
    loadChildren: () => import('./Components/contract/add-new-contract/add-new-contract.module').then(auth=> auth.AddNewContractModule)
  },
  {
    path: 'edit-highlight-contract',
    loadChildren: () => import('./Components/contract/highlighter-pdf/highlighter-pdf.module').then(auth => auth.HighlighterPDFModule),
  },
  {
    path: 'users',
    loadChildren: () => import('./Components/users/users.module').then(auth => auth.UsersModule),
    canActivate: [spendkeyStaffGuard('admin')]
  },
  {
    path: 'reclassification',
    loadChildren: () => import('./Components/spend-reclassification/spend-reclassification.module').then(auth => auth.SpendReclassificationModule),
    canActivate: [reclassificationGuard],
  },
  {
    path: 'document-analytics',
    loadComponent: () => import('./Components/document-analytics/document-analytics.component').then(m => m.DocumentAnalyticsComponent),
    canActivate: [userGuard],
  },
  {
    path: 'document-extraction',
    loadComponent: () => import('./Components/document-extraction/document-extraction.component').then(m => m.DocumentExtractionComponent),
    canActivate: [userGuard],
  },
  {
    path: 'document-hierarchy',
    loadComponent: () => import('./Components/document-hierarchy/document-hierarchy.component').then(m => m.DocumentHierarchyComponent),
    canActivate: [userGuard],
  },
  {
    path: 'settings',
    loadChildren: () => import('./Components/settings/settings.module').then(auth => auth.SettingsModule),
    canActivate: [spendkeyStaffGuard('admin')]
  },
  {
    path: 'menu-settings',
    loadChildren: () => import('./Components/menu-settings/menu-settings.module').then(m => m.MenuSettingsModule),
    canActivate: [spendkeyStaffGuard('admin')]
  },
  {
    path: 'cassian-ai',
    loadChildren: () => import('./Components/cassian-ai/cassian-ai.module').then(auth => auth.CassianAiModule)
  },
  {
    path: 'executive-overview',
    component: ExecutiveOverviewComponent
  },
  {
    path: 'spend-analytics',
    component: SpendAnalyticsComponent
  },
  {
    // Spendkey-staff only, matching /savings-settings and the admin-console
    // Workflow group. The page runs the AI pipelines against a tenant database
    // resolved from the client→database mapping, and its Manage Companies dialog
    // edits that mapping — neither is a tenant-facing surface.
    //
    // Staff at any role: the requirement here is the staff axis alone.
    //
    // NOTE: this is a usability gate, not a security boundary. The page calls
    // SKeyAiCoreService directly with `SKEY_AI_API_KEY`, which is compiled into
    // the bundle and readable in DevTools by any signed-in user, so it
    // authenticates the frontend rather than the caller. A real boundary needs
    // server-side identity on that service.
    path: 'ai-recommendations',
    canActivate: [spendkeyStaffGuard()],
    loadComponent: () => import('./Components/ai-recommendations/ai-recommendations.component').then(auth => auth.AiRecommendationsComponent)
  },
  {
    // Client-facing savings-recommendations dashboard (reads the persisted nuance report
    // via the .NET /api/SavingsRecommendations read API). Store is scoped in the lazy routes.
    // No userGuard — that guard blocks Member-role users, which excludes the exact
    // finance / reviewer personas this dashboard is aimed at.
    // Gated on `savings.opportunities.report.view` plus the tblPage row (#see guard).
    // Still no userGuard, for the reason above: Member-role users are the audience.
    path: 'savings-recommendations',
    canActivate: [savingsRecommendationsGuard],
    loadChildren: () => import('./Components/savings-recommendations/savings-recommendations.routes').then(m => m.SAVINGS_RECOMMENDATIONS_ROUTES),
  },
  {
    path: 'tier2-upload',
    loadComponent: () => import('./Components/tier2-upload/tier2-upload.component').then(auth => auth.Tier2UploadComponent)
  },
  {
    // Internal DA-team tool — gated to Spendkey staff (any role). External
    // clients log into these envs for reports and
    // must NOT reach this page. NOTE: this guard only hides the page client-side;
    // the underlying AI-core endpoints (/tier2/validate-internal, /internal-download)
    // are protected only by the shared X-API-Key the interceptor injects for every
    // authenticated SPA user — there is no server-side staff check. If staff-only
    // access to internal outputs is a hard requirement, it must be enforced in the
    // AI core, not here.
    path: 'internal-classification',
    canActivate: [spendkeyStaffGuard()],
    loadComponent: () => import('./Components/internal-classification/internal-classification.component').then(m => m.InternalClassificationComponent)
  },
  {
    path: 'upload',
    loadComponent: () => import('./Components/upload/upload.component').then(m => m.UploadComponent)
  },
  {
    // Spendkey-staff only, same treatment as /ai-recommendations: it monitors the AI
    // pipeline's job queue across tenants, which is an operations surface rather than
    // a tenant one. Same guard as /ai-recommendations so the two cannot drift apart.
    //
    // Usability gate, not a security boundary — the page calls SKeyAiCoreService
    // directly with `SKEY_AI_API_KEY`, which is compiled into the bundle and readable
    // in DevTools, so it authenticates the frontend rather than the caller.
    path: 'job-monitor',
    canActivate: [spendkeyStaffGuard()],
    loadComponent: () => import('./Components/job-monitor/job-monitor.component').then(m => m.JobMonitorComponent)
  },
  {
    path: 'pricing-index-dashboard',
    loadComponent: () => import('./Components/pricing-index-dashboard/pricing-index-dashboard.component').then(m => m.PricingIndexDashboardComponent),
    canActivate: [userGuard],
    providers: [{ provide: PRICING_INDEX_PROFILE, useValue: MEPPI_PROFILE }],
  },
  {
    path: 'pricing-index-dashboard-demo',
    loadComponent: () => import('./Components/pricing-index-dashboard/pricing-index-dashboard.component').then(m => m.PricingIndexDashboardComponent),
    canActivate: [userGuard],
    providers: [{ provide: PRICING_INDEX_PROFILE, useValue: DEMO_PROFILE }],
  },
  { path: 'attachments-demo',
    loadComponent: () => import('./Components/attachments-demo/attachments-demo.component').then(m => m.AttachmentsDemoComponent),
    canActivate: [spendkeyStaffGuard('admin')]
  },
  {
    path: 'demo',
    loadComponent: () => import('./Components/ai-classification-stepper/ai-classification-stepper.component').then(auth => auth.AiClassificationStepperComponent)
  },
  {
    path: 'contract-extraction',
    loadComponent: () => import('./Components/contract-extraction/contract-extraction.component').then(m => m.ContractExtractionComponent)
  },
  {
    path: 'contract-extraction-single',
    loadComponent: () => import('./Components/contract-extraction-single/contract-extraction-single.component').then(m => m.ContractExtractionSingleComponent)
  },
  {
    // SuperAdmin-only enabler admin surface (US #9320). Parent guard gates all
    // child routes (audit / rbac / workflow); F5 (#9348) adds the sidebar entry.
    path: 'admin-console',
    canActivate: [superAdminGuard],
    loadChildren: () =>
      import('./Components/admin-console/admin-console.routes').then(m => m.ADMIN_CONSOLE_ROUTES),
  },
  {
    path: 'integrity-next',
    loadComponent: () => import('./Components/integrity-next/integrity-next-list.component').then(m => m.IntegrityNextListComponent),
    canActivate: [spendkeyStaffGuard('admin')],
  },
  {
    // Rewrites enrichment columns on a client fact table, so staff admins only, matching
    // [RequireSpendkeyStaff(Admin)] on every AdhocEnrichment action.
    path: 'adhoc-enrichment',
    loadComponent: () => import('./Components/adhoc-enrichment/adhoc-enrichment.component').then(m => m.AdhocEnrichmentComponent),
    canActivate: [spendkeyStaffGuard('admin')],
  },
  // Report destinations, `/dashboard` included — each renders the tenant's Power
  // BI report filtered to the pages assigned to it (tblPageFields
  // `Type='PbiPage'`). The five guarded ones are inert for any tenant without the
  // matching `tblPage` row: no sidebar entry, and reportDestinationGuard turns a
  // hand-typed URL away. The `path` must stay equal to `tblPage.Link` — the guard
  // matches on it. They share one parent so switching keeps the embed alive.
  ...REPORT_DESTINATION_ROUTES,
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full'
  },
  {
    path: '**',
    component: LoginComponent
  },
];
